import { Flex } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { useConfigGuard } from "../../contexts/AppContext";
import { Breadcrumb, IBreadcrumbStep } from "../Breadcrumb/Breadcrumb";
import { rotaDoCaminhoPublicado, useCaminhoDoModulo } from "./moduleTrail";
import { resolveTrail } from "./trailSteps";
import { useCertificateName } from "./useCertificateName";

/**
 * Trilha da barra global (GEN-3497).
 *
 * Ela mora **na mesma linha dos botões globais**, como no Notion: a faixa de 48px já existe
 * para a busca e o menu de conta, então a trilha viaja de graça ali e a página começa direto
 * no título. Na r6 ela desceu para dentro da página e o Thiago reprovou: duas linhas de
 * cabeçalho (trilha e título) comiam altura útil de toda tela do sistema.
 *
 * Quem escreve o NOME da tela é o `PageHeader`, dentro da página. Aqui é só o caminho.
 *
 * r3: o CLIENTE saiu da trilha. Quase todo usuário da Taxly pertence a um cliente só, e
 * para eles o primeiro degrau era ruído repetido em toda página. A trilha passa a dizer só
 * ONDE você está dentro do produto; em nome de quem é assunto do menu de conta — o mesmo
 * racional do Notion.
 *
 * A aparência não mora aqui: quem desenha é o `Breadcrumb` compartilhado
 * (`src/components/Breadcrumb`), o padrão de trilha do sistema. Este componente só resolve
 * QUAIS degraus existem para a rota atual.
 *
 * O primeiro degrau (o cliente) fica de fora do `Breadcrumb` porque é interativo de um
 * jeito próprio — abre um menu com busca, não navega.
 */
export const Trail = () => {
  const { pathname, search } = useLocation();
  const { certificateName, catalogoCarregado } = useCertificateName();
  const { requestNavigation } = useConfigGuard();

  // Módulo embutido (Base Cadastral, Kanban 2.0) publica o caminho dele, porque conhece degraus que a
  // rota não revela — qual empresa está aberta, por exemplo. Quando há caminho publicado, ele
  // VENCE o que o host resolveria sozinho: é mais específico e é o próprio módulo falando.
  //
  // Antes o módulo desenhava a trilha dentro da página e o host escondia a dele para não duplicar.
  // Isso deixava o caminho num lugar diferente do resto do sistema, rolando junto com o conteúdo,
  // e a faixa vazia naquelas telas. Agora o desenho é um só, aqui, para todos os módulos.
  const caminhoDoModulo = useCaminhoDoModulo();

  // A comparação com a rota mora AQUI, não dentro do store: o `pathname` vem do roteador, e o
  // snapshot do store fica sendo só o estado do store — que é o contrato do `useSyncExternalStore`
  // (ler uma fonte externa lá dentro faz o snapshot mudar sem a store notificar ninguém).
  const publicadoParaEstaTela =
    caminhoDoModulo.length > 0 && pathname.startsWith("/" + (rotaDoCaminhoPublicado().split("/").filter(Boolean)[0] ?? ""));

  const steps: IBreadcrumbStep[] = publicadoParaEstaTela
    ? caminhoDoModulo.map((degrau) => ({
        label: degrau.label,
        // `aoAbrir` vem de módulo com roteador próprio (Kanban 2.0) e tem precedência: navegar pelo
        // host ali trocaria a URL sem trocar a tela.
        onClick: degrau.aoAbrir ?? (degrau.to ? () => requestNavigation(degrau.to as string) : undefined),
      }))
    : resolveTrail(pathname, search, certificateName, catalogoCarregado).map((step) => ({
        label: step.label,
        onClick: step.path ? () => requestNavigation(step.path as string) : undefined,
      }));

  // Sem degraus não renderiza nada: um <nav> vazio continua sendo um landmark de navegação para
  // leitor de tela, anunciando um caminho que não existe.
  //
  // UM degrau também não renderiza. Caminho de um degrau só não mostra hierarquia nenhuma — ele
  // repete, palavra por palavra, o título que a página escreve logo abaixo. Pela heurística de
  // design minimalista (Nielsen 8), informação repetida compete com a relevante e reduz a
  // visibilidade dela; e trilha é navegação secundária, que só passa a informar de dois níveis em
  // diante. Assim o caminho aparecer PASSA A SIGNIFICAR ALGO: você desceu um nível.
  if (steps.length <= 1) return null;

  return (
    <Flex align="center" minW="0" flexShrink={1} overflow="hidden">
      <Breadcrumb steps={steps} />
    </Flex>
  );
};
